import { lazy } from 'react';

const RELOAD_FLAG = 'lazy-with-retry:reloaded';
export const RELOAD_TIMEOUT_MS = 15000;

type LazyFactory = Parameters<typeof lazy>[0];

// sessionStorage access can throw (e.g. disabled by browser privacy settings
// or an extension). Treat that as "already reloaded" so the error surfaces
// immediately instead of retrying forever.
function hasAlreadyReloaded(): boolean {
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG) === 'true';
  } catch {
    return true;
  }
}

function markReloaded(): void {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, 'true');
  } catch {
    // Storage unavailable - nothing to persist, retry guard falls back to
    // hasAlreadyReloaded() returning true above.
  }
}

function clearReloadFlag(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // Storage unavailable - nothing to clear.
  }
}

/**
 * Wraps `React.lazy` so that a failed dynamic import (typically a stale chunk
 * after a new deploy replaced the hashed file names) triggers a single full
 * page reload. A sessionStorage flag prevents an infinite reload loop when the
 * import keeps failing for another reason (e.g. the user is offline).
 */
export function lazyWithRetry(factory: LazyFactory): ReturnType<typeof lazy> {
  return lazy(async () => {
    try {
      const component = await factory();
      clearReloadFlag();

      return component;
    } catch (error) {
      if (!hasAlreadyReloaded()) {
        markReloaded();
        window.location.reload();

        // The page is reloading and React should not render. If the reload
        // is somehow blocked (e.g. a beforeunload prompt), surface the
        // original error instead of leaving the Suspense boundary stuck.
        return new Promise<Awaited<ReturnType<LazyFactory>>>((_, reject) => {
          setTimeout(() => reject(error), RELOAD_TIMEOUT_MS);
        });
      }

      throw error;
    }
  });
}
